import React, { useState, useEffect } from 'react'
// @ts-ignore
import Modal from '../../shared/modal'
import { LocalizationContextType } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import LevelBadge, { getLevelBadgeClass } from '../shared/LevelBadge'
import { TIME_SOURCE, timeSourceTitle } from '../../../utils/timeSource'

interface Tag {
  [key: string]: string | boolean | number
}

interface SentryAlert {
  id: number
  message: string
  level: string
  tags: Tag[]
  sent_at: string
  business?: boolean
  location_name?: string | null
  organization_name?: string | null
}

interface SentryAlertsTableProps {
  sentry_alerts: SentryAlert[]
  is_scoped: boolean
  localization_context?: LocalizationContextType
}

const SentryAlertsTable: React.FC<SentryAlertsTableProps> = ({ sentry_alerts, is_scoped, localization_context }) => {
  const [selectedAlert, setSelectedAlert] = useState<SentryAlert | null>(null)

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedAlert) {
        setSelectedAlert(null)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [selectedAlert])

  const formatDate = (dateString: string) => formatTenantDateTime(dateString, localization_context)

  const humanizeTagKey = (key: string): string => {
    return key
      .split('_')
      .map(word => {
        if (word.toLowerCase() === 'id') return 'ID'
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
      .join(' ')
  }

  const renderDetailsModal = () => {
    if (!selectedAlert) return null

    const modalId = 'sentry-alert-details-modal'
    const headerId = 'sentry-alert-details-header'

    return (
      <Modal
        headerText={`Alert Details`}
        onClose={() => setSelectedAlert(null)}
        widen={true}
        aria-modal="true"
        aria-labelledby={headerId}
        role="dialog"
        id={modalId}
      >
        <div className="sentry-alert-detail" role="document">
          <h2 id={headerId} className="visually-hidden">Alert Details</h2>
          <div className="mb-3">
            <strong>Level:</strong>{' '}
            <span className={getLevelBadgeClass(selectedAlert.level)} style={{ color: '#FFFFFF'}}>
              {selectedAlert.level?.toUpperCase()}
            </span>
          </div>

          <div className="mb-3">
            <strong>Sent At:</strong>{' '}
            <span title={timeSourceTitle(selectedAlert.sent_at, TIME_SOURCE.app)}>{formatDate(selectedAlert.sent_at)}</span>
          </div>

          <div className="mb-3">
            <strong>Message:</strong>
            <div className="mt-1 p-2 bg-light rounded">
              <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {selectedAlert.message}
              </code>
            </div>
          </div>

          {!is_scoped && (
            <>
              <div className="mb-3">
                <strong>Business Alert:</strong>{' '}
                <span className={`badge ${selectedAlert.business ? 'bg-success' : 'bg-secondary'}`}>
                  {selectedAlert.business ? 'Yes' : 'No'}
                </span>
              </div>

              {selectedAlert.organization_name && (
                <div className="mb-3">
                  <strong>Organization:</strong>{' '}
                  <span>{selectedAlert.organization_name}</span>
                </div>
              )}

              {selectedAlert.location_name && (
                <div className="mb-3">
                  <strong>Location:</strong>{' '}
                  <span>{selectedAlert.location_name}</span>
                </div>
              )}
            </>
          )}

          {selectedAlert.tags && selectedAlert.tags.length > 0 && (
            <div>
              <strong>All Tags:</strong>
              <div className="table-responsive mt-2">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedAlert.tags.map((tag, index) => {
                      const [key, value] = Object.entries(tag)[0] || []
                      return (
                        <tr key={index}>
                          <td><strong>{humanizeTagKey(key)}</strong></td>
                          <td>{String(value)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal>
    )
  }

  return (
    <>
      <table className="table table-robis">
        <thead className="thead-robis">
          <tr>
            <th scope="col">Date & Time</th>
            <th scope="col">Level</th>
            <th scope="col">Message</th>
            <th scope="col">Business</th>
            {!is_scoped && (
              <>
                <th scope="col">Organization</th>
                <th scope="col">Location</th>
              </>
            )}
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sentry_alerts.map((alert) => (
            <tr key={alert.id}>
              <td>
                <div className="small" title={timeSourceTitle(alert.sent_at, TIME_SOURCE.app)}>
                  {formatDate(alert.sent_at)}
                </div>
              </td>
              <td>
                <LevelBadge level={alert.level} />
              </td>
              <td>
                <div
                  className="small"
                  style={{
                    maxWidth: '400px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={alert.message}
                >
                  {alert.message}
                </div>
              </td>
              <td>
                <span className={`badge ${alert.business ? 'bg-success' : 'bg-secondary'}`}>
                  {alert.business ? 'Yes' : 'No'}
                </span>
              </td>
              {!is_scoped && (
                <>
                  <td>
                    {alert.organization_name || <span className="text-muted">-</span>}
                  </td>
                  <td>
                    {alert.location_name || <span className="text-muted">-</span>}
                  </td>
                </>
              )}
              <td>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setSelectedAlert(alert)}
                >
                  Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {renderDetailsModal()}
    </>
  )
}

export default SentryAlertsTable
